import {isNil} from 'ramda';

import {BusinessCaseResponseBody} from '@dms/api/sales';
import {Money} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {getTradeInCounterpartPrice} from './getTradeInCounterpartPrice';

interface GetTradeInOffsetPriceParams {
  /** Base price the offset is calculated from — the sale order total or the BC headline price. */
  baseAmount: string | Nullish;
  /** Currency to denominate the offset in; without it there is nothing to render. */
  baseCurrency: string | Nullish;
  /** The linked trade-in counterpart BC; its agreed purchase price offsets the base price. */
  counterpart: BusinessCaseResponseBody | Nullish;
}

/**
 * Trade-in offset = base price minus the counterpart's agreed purchase price. A missing amount on
 * either side counts as 0. The base differs by call site (the Checkout uses the order Total price,
 * the linked-state card uses the BC primary parameter), but the arithmetic is identical.
 *
 * Returns `undefined` when there is no currency to denominate the result in, letting callers
 * fall back to their own empty display (a `PriceBox` placeholder, `EMPTY_PLACEHOLDER`, ...).
 */
export const getTradeInOffsetPrice = (params: GetTradeInOffsetPriceParams): Money | undefined => {
  if (isNil(params.baseCurrency)) {
    return undefined;
  }

  const counterpartAmount = getTradeInCounterpartPrice(params.counterpart)?.amount;
  const offsetAmount = Number(params.baseAmount ?? 0) - Number(counterpartAmount ?? 0);

  return {amount: String(offsetAmount), currency: params.baseCurrency};
};
